import React, { useEffect, useRef } from "react";
import {
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  IconButton,
} from "@chakra-ui/react";
import { SearchIcon, CloseIcon } from "@chakra-ui/icons";

interface SearchBarProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  placeholderText: string;
  autoFocus?: boolean;
}

const SearchBar: React.FC<SearchBarProps> = ({
  searchQuery,
  onSearchChange,
  placeholderText = "Search...",
  autoFocus = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const lastFocusTimeRef = useRef<number>(0);
  const lastTypeTimeRef = useRef<number>(0);


  useEffect(() => {
    const handleScroll = () => {
      // Only blur if the input is focused and it's been more than 300ms since focus
      // and more than 500ms since last typing
      if (
        document.activeElement === inputRef.current &&
        Date.now() - lastFocusTimeRef.current > 300 &&
        Date.now() - lastTypeTimeRef.current > 500
      ) {
        inputRef.current?.blur();
      }
    };

    // Add scroll event listener
    window.addEventListener("scroll", handleScroll, { passive: true });

    // Cleanup
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleFocus = () => {
    lastFocusTimeRef.current = Date.now();
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    lastTypeTimeRef.current = Date.now();
    onSearchChange(event.target.value);
  };

  const handleClear = () => {
    onSearchChange("");
  };

  return (
    <InputGroup bg={"white"} border={"none"} rounded={"xl"} mb={1}>
      <InputLeftElement>
        <SearchIcon />
      </InputLeftElement>
      <Input
        ref={inputRef}
        // iOS opens the keyboard immediately when the search bar appears
        // (autoFocus={isSearchVisible}); React focuses it on mount declaratively.
        autoFocus={autoFocus}
        placeholder={placeholderText}
        bg={"white"}
        border={"none"}
        value={searchQuery}
        onChange={handleChange}
        onFocus={handleFocus}
      />
      {searchQuery && (
        <InputRightElement>
          <IconButton
            aria-label="Clear search"
            icon={<CloseIcon />}
            size="sm"
            variant="ghost"
            onClick={handleClear}
            _hover={{ bg: "gray.100" }}
          />
        </InputRightElement>
      )}
    </InputGroup>
  );
};

export default SearchBar;
